import { createSlice } from '@reduxjs/toolkit';

// Time-of-day theme: light during the day, dark at night. 06:00–17:59 → light, 18:00–05:59 → dark.
const timeBasedMode = () => {
  const h = new Date().getHours();
  return h >= 6 && h < 18 ? 'light' : 'dark';
};

const VALID_PREFS = ['auto', 'light', 'dark'];

const getInitialPreference = () => {
  const saved = localStorage.getItem('themePref');
  if (VALID_PREFS.includes(saved)) return saved;
  // New default: follow the time of day. (The legacy 'theme' key is intentionally not used as a
  // preference, so everyone gets the auto behavior; a manual override is remembered under 'themePref'.)
  return 'auto';
};

// The applied light/dark value for a given preference — for 'auto' it depends on the current time.
const resolveMode = (pref) => (pref === 'auto' ? timeBasedMode() : pref);

const applyDom = (mode) => {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', mode === 'dark');
};

const initialPreference = getInitialPreference();

const initialState = {
  preference: initialPreference,          // 'auto' | 'light' | 'dark' — the user's intent
  mode: resolveMode(initialPreference),   // 'light' | 'dark' — what is actually applied
};

const persistAndApply = (state, pref) => {
  state.preference = pref;
  localStorage.setItem('themePref', pref);
  state.mode = resolveMode(pref);
  applyDom(state.mode);
};

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    // Explicit user choice: 'auto', 'light', or 'dark'.
    setPreference: (state, action) => {
      persistAndApply(state, VALID_PREFS.includes(action.payload) ? action.payload : 'auto');
    },
    // Manual light/dark flip = an explicit override (leaves 'auto').
    toggleTheme: (state) => {
      persistAndApply(state, state.mode === 'light' ? 'dark' : 'light');
    },
    // Backward-compat: setTheme('light'|'dark'|'auto') sets an explicit preference.
    setTheme: (state, action) => {
      persistAndApply(state, VALID_PREFS.includes(action.payload) ? action.payload : 'auto');
    },
    // Re-resolve the applied mode from the current preference + clock. Called on load and on an
    // interval so 'auto' flips at 6am / 6pm without needing a page reload.
    refreshTheme: (state) => {
      const mode = resolveMode(state.preference);
      state.mode = mode;
      applyDom(mode);
    },
  },
});

export const { setPreference, toggleTheme, setTheme, refreshTheme } = themeSlice.actions;
export default themeSlice.reducer;
